import axios from "axios";
import { createContext, useContext, useMemo, useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { useCookie } from "./useCookieStorage";

const SESSION_EXPIRED_EVENT = "hanypay:session-expired";
const SESSION_EXPIRED_KEY = "hanypay:session-expired";

type UserToken = {
  access?: string;
  refresh?: string;
  status?: boolean;
};

type AuthContextType = {
  user: UserToken | null;
  isInitialized: boolean;
  login: (data: UserToken) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserToken | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [accessToken, setAccessToken, removeAccessToken] = useCookie("access", "");
  const [refreshToken, setRefreshToken, removeRefreshToken] = useCookie("refresh", "");
  const navigate = useNavigate();
  const location = useLocation();

  // Function to decode JWT and get expiration time
  const getTokenExpirationDate = useCallback((token: string): Date | null => {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      if (decoded.exp) {
        return new Date(decoded.exp * 1000);
      }
    } catch (error) {
      console.error("Failed to decode JWT token", error);
    }
    return null;
  }, []);

  // Function to check if token is expired
  const isTokenExpired = useCallback((token: string): boolean => {
    const expirationDate = getTokenExpirationDate(token);
    if (expirationDate) {
      return expirationDate < new Date();
    }
    return true;
  }, [getTokenExpirationDate]);

  const logout = useCallback(() => {
    setUser(null);
    removeAccessToken();
    removeRefreshToken();

    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common.Authorization;

    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    }

    return navigate("/", { replace: true });
  }, [removeAccessToken, removeRefreshToken, navigate]);

  // Logout only when the refresh token is gone or expired.
  const checkTokenExpiration = useCallback(() => {
    if (refreshToken && isTokenExpired(refreshToken)) {
      logout();
      return;
    }

    if (!refreshToken && accessToken && isTokenExpired(accessToken)) {
      logout();
    }
  }, [accessToken, refreshToken, isTokenExpired, logout]);

  useEffect(() => {
    // Initialize user state from cookies or localStorage on component mount
    if (typeof window !== "undefined" && window.sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1") {
      logout();
      setIsInitialized(true);
      return;
    }

    const storedAccessToken = localStorage.getItem("access") || accessToken;
    const storedRefreshToken = localStorage.getItem("refresh") || refreshToken;

    if (storedAccessToken && storedRefreshToken) {
      setUser({ access: storedAccessToken, refresh: storedRefreshToken });
    }
    setIsInitialized(true);

    // Set an interval to check token expiration
    const interval = setInterval(() => {
      checkTokenExpiration();
    }, 60000); // Check every minute

    const handleAuthUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ access?: string; refresh?: string }>;
      const nextAccess = customEvent.detail?.access || "";
      const nextRefresh = customEvent.detail?.refresh || "";

      if (nextAccess) {
        setAccessToken(nextAccess);
        axios.defaults.headers.common.Authorization = `Bearer ${nextAccess}`;
      }

      if (nextRefresh) {
        setRefreshToken(nextRefresh);
      }

      if (nextAccess && nextRefresh) {
        setUser({ access: nextAccess, refresh: nextRefresh });
      }
    };

    const handleSessionExpired = () => {
      logout();
    };

    window.addEventListener("hanypay:auth-updated", handleAuthUpdate as EventListener);
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      clearInterval(interval);
      window.removeEventListener("hanypay:auth-updated", handleAuthUpdate as EventListener);
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [accessToken, refreshToken, checkTokenExpiration, setAccessToken, setRefreshToken, logout]);

  const login = useCallback((data: UserToken) => {
    setUser(data);
    setAccessToken(data.access || "");
    setRefreshToken(data.refresh || "");

    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    }

    if (data.access) {
      axios.defaults.headers.common.Authorization = `Bearer ${data.access}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }

    localStorage.setItem("access", data.access || "");
    localStorage.setItem("refresh", data.refresh || "");
    localStorage.setItem(
      "user",
      JSON.stringify({
        access: data.access || "",
        refresh: data.refresh || "",
      }),
    );

    // Redirect to the intended destination or default to /home/dashboard
    const from = location.state?.from?.pathname || "/home/dashboard";
    navigate(from, { replace: true });
  }, [setAccessToken, setRefreshToken, location.state, navigate]);



  const value = useMemo(
    () => ({
      user,
      isInitialized,
      login,
      logout,
    }),
    [user, isInitialized, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
